import type { ShipperApplicationInput, ShipperDocument, ShipperProfileView, ShipperShift, ShipperSignupOptions, VehicleType } from '@khaddi/contracts';
import { SHIPPER_DOCUMENT_LABELS } from '@khaddi/contracts';
import { Button, Checkbox, colors, brandSurface, ErrorNote, Field, FooterBar, Icon, LogoMark, Screen, Spinner, Stack, Text, TopBar, useAction, useApi, useAsync, useRouter, useSession } from '@khaddi/ui';
import { useState, type ReactNode } from 'react';
import { Linking, Pressable, View } from 'react-native';
import { InlineError, OptionPill } from '../components/bits';
import { useTabNav } from '../components/TabNav';
import { pickImage, uploadPlaceholder } from '../components/pickImage';
import { SHIPPER_APP_URL } from '../config';

const STEPS = ['1. Thông tin', '2. Giấy tờ', '3. Nhận tiền'] as const;
const VEHICLE_DOCS: ShipperDocument[] = ['idFront', 'idBack', 'license', 'portrait'];
const DOC_HINT: Record<ShipperDocument, string> = { idFront: 'Chụp ảnh', idBack: 'Chụp ảnh', license: 'Chụp ảnh', portrait: 'Chụp selfie', bankAccount: '' };

/** Bicycles need neither a plate nor a licence. */
const needsLicence = (vehicle: VehicleType) => vehicle !== 'Xe đạp';

export const ShipperSignupScreen = () => {
  const nav = useTabNav('account', { wideOnly: true });
  const api = useApi();
  const { back } = useRouter();
  const state = useAsync(async () => {
    const [me, options] = await Promise.all([api.shipper.me(), api.shipper.signupOptions()]);
    return { profile: me.profile, options };
  }, [api]);

  if (state.loading && !state.data) {
    return (
      <Screen background={colors.surface} nav={nav} maxWidth={720}>
        <TopBar title="Đăng ký làm shipper" onBack={() => back('/account')} />
        <Spinner />
      </Screen>
    );
  }
  if (!state.data) {
    return (
      <Screen background={colors.surface} nav={nav} maxWidth={720}>
        <TopBar title="Đăng ký làm shipper" onBack={() => back('/account')} />
        <ErrorNote message={state.error ?? 'Không tải được thông tin'} onRetry={() => void state.reload()} />
      </Screen>
    );
  }
  const data = state.data;
  if (data.profile) return <ApplicationStatus profile={data.profile} />;
  return <SignupForm options={data.options} onApplied={(profile) => state.setData({ ...data, profile })} />;
};

const Intro = () => (
  <View style={{ ...brandSurface, borderRadius: 20, padding: 16, overflow: 'hidden' }}>
    <View pointerEvents="none" style={{ position: 'absolute', right: -30, bottom: -40 }}>
      <LogoMark size={140} opacity={0.45} />
    </View>
    <View style={{ gap: 4 }}>
      <Text variant="title" color={colors.text} style={{ fontSize: 20, lineHeight: 23 }}>
        Giao trà cùng Khao Ddi
      </Text>
      <Text variant="caption" color={colors.text} style={{ lineHeight: 18 }}>
        Chủ động giờ chạy · nhận tiền theo ngày · đơn gần quán, quãng ngắn
      </Text>
    </View>
  </View>
);

const SignupForm = ({ options, onApplied }: { options: ShipperSignupOptions; onApplied: (profile: ShipperProfileView) => void }) => {
  const nav = useTabNav('account', { wideOnly: true });
  const api = useApi();
  const { user } = useSession();
  const { back } = useRouter();
  const [step, setStep] = useState(0);
  const [showErrors, setShowErrors] = useState(false);

  const [name, setName] = useState(user?.name ?? '');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleType, setVehicleType] = useState<VehicleType>(options.vehicleTypes[0] ?? 'Xe máy');
  const [plate, setPlate] = useState('');
  const [areas, setAreas] = useState<string[]>([]);
  const [shifts, setShifts] = useState<ShipperShift[]>([]);
  const [documents, setDocuments] = useState<Partial<Record<ShipperDocument, string>>>({});
  const [bankAccount, setBankAccount] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const requiredDocs = VEHICLE_DOCS.filter((d) => d !== 'license' || needsLicence(vehicleType));
  const stepErrors: (string | null)[] = [
    name.trim().length >= 2 ? null : 'Nhập họ tên (ít nhất 2 ký tự)',
    requiredDocs.some((d) => !documents[d])
      ? 'Tải lên đủ giấy tờ bắt buộc'
      : needsLicence(vehicleType) && !plate.trim()
        ? 'Nhập biển số xe'
        : areas.length === 0
          ? 'Chọn ít nhất một khu vực'
          : shifts.length === 0
            ? 'Chọn ít nhất một ca'
            : null,
    bankAccount.trim().length < 6 ? 'Nhập số tài khoản nhận tiền' : !acceptTerms ? 'Cần đồng ý quy định hợp tác' : null,
  ];

  const apply = useAction(async () => {
    const input: ShipperApplicationInput = {
      name: name.trim(),
      vehicleType,
      vehicleModel: vehicleModel.trim(),
      plate: plate.trim().toUpperCase(),
      areas,
      shifts,
      // The console reads the payout account from the documents checklist as well.
      documents: { ...documents, bankAccount: bankAccount.trim() },
      bankAccount: bankAccount.trim(),
      acceptTerms: true,
    };
    onApplied(await api.shipper.apply(input));
  });

  const next = () => {
    if (stepErrors[step]) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    if (step < STEPS.length - 1) setStep(step + 1);
    else void apply.run();
  };

  const upload = async (doc: ShipperDocument) => {
    const file = await pickImage();
    if (file) setDocuments((d) => ({ ...d, [doc]: uploadPlaceholder(file) }));
  };

  const toggle = <T,>(list: T[], value: T): T[] => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  const error = showErrors ? stepErrors[step] : null;

  return (
    <Screen
      background={colors.surface}
      nav={nav}
      maxWidth={720}
      footer={
        <FooterBar style={{ paddingHorizontal: 20 }}>
          {step === STEPS.length - 1 ? (
            <Checkbox
              checked={acceptTerms}
              onChange={setAcceptTerms}
              label={
                <Text variant="caption">
                  Tôi đồng ý với <Text variant="caption" color={colors.primaryText} style={{ fontWeight: '600' }}>quy định hợp tác giao hàng</Text>
                </Text>
              }
            />
          ) : null}
          <InlineError message={error ?? apply.error} />
          <Button label={step === STEPS.length - 1 ? 'Gửi đăng ký' : 'Tiếp tục'} onPress={next} loading={apply.pending} />
        </FooterBar>
      }
    >
      <View style={{ height: 60, flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8 }}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={step > 0 ? 'Bước trước' : 'Quay lại'}
          onPress={() => (step > 0 ? setStep(step - 1) : back('/account'))}
          style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
        >
          <Icon name="chevronLeft" size={22} color={colors.text} strokeWidth={2.2} />
        </Pressable>
        <Text variant="title" style={{ fontSize: 20 }}>
          Đăng ký làm shipper
        </Text>
      </View>

      <View style={{ paddingHorizontal: 20, gap: 16 }}>
        <Intro />
        <View accessibilityRole="list" aria-label="Các bước đăng ký" style={{ flexDirection: 'row', gap: 6 }}>
          {STEPS.map((label, i) => (
            <View key={label} accessibilityRole="listitem" aria-current={i === step ? 'step' : undefined} style={{ flex: 1, gap: 6 }}>
              <View style={{ height: 6, borderRadius: 3, backgroundColor: i <= step ? colors.primary : colors.border }} />
              <Text variant="caption" color={i <= step ? colors.text : colors.textMuted} style={{ fontWeight: i <= step ? '700' : '500' }}>
                {label}
              </Text>
            </View>
          ))}
        </View>

        {step === 0 ? (
          <Stack gap={14}>
            <Text variant="heading" style={{ fontSize: 16 }}>
              Thông tin của bạn
            </Text>
            <Field label="Họ tên (như trên CCCD)" value={name} onChangeText={setName} autoComplete="name" maxLength={80} />
            <Field label="Số điện thoại nhận đơn" value={user?.phone ?? ''} editable={false} style={{ backgroundColor: colors.background }} />
            <Text variant="label" style={{ fontWeight: '400' }}>
              Dùng số này để đăng nhập ứng dụng shipper sau khi được duyệt.
            </Text>
          </Stack>
        ) : null}

        {step === 1 ? (
          <Stack gap={16}>
            <Text variant="heading" style={{ fontSize: 16 }}>
              Giấy tờ & phương tiện
            </Text>
            <Grid>
              {requiredDocs.map((doc) => (
                <DocumentTile key={doc} label={labelFor(options, doc)} hint={DOC_HINT[doc]} value={documents[doc]} onPress={() => void upload(doc)} />
              ))}
            </Grid>
            <Labeled label="Loại xe">
              <View accessibilityRole="radiogroup" style={{ flexDirection: 'row', gap: 8 }}>
                {options.vehicleTypes.map((v) => (
                  <OptionPill key={v} label={v} selected={vehicleType === v} onPress={() => setVehicleType(v)} style={{ flex: 1 }} height={44} />
                ))}
              </View>
            </Labeled>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <Field label="Biển số xe" placeholder="43B1-xxx.xx" value={plate} onChangeText={setPlate} autoCapitalize="characters" maxLength={20} />
              <Field label="Dòng xe" placeholder="Honda Vision" value={vehicleModel} onChangeText={setVehicleModel} maxLength={40} />
            </View>
            <Labeled label="Khu vực muốn nhận đơn">
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {options.areas.map((a) => (
                  <OptionPill key={a} label={a} selected={areas.includes(a)} onPress={() => setAreas((l) => toggle(l, a))} height={38} style={{ paddingHorizontal: 14, borderWidth: 1.5 }} />
                ))}
              </View>
            </Labeled>
            <Labeled label="Ca có thể chạy">
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {options.shifts.map((s) => (
                  <OptionPill
                    key={s.id}
                    label={s.label}
                    sub={s.time}
                    fill={false}
                    selected={shifts.includes(s.id)}
                    onPress={() => setShifts((l) => toggle(l, s.id))}
                    height={52}
                    style={{ flex: 1, borderRadius: 12, borderWidth: 1.5 }}
                  />
                ))}
              </View>
            </Labeled>
          </Stack>
        ) : null}

        {step === 2 ? (
          <Stack gap={14}>
            <Text variant="heading" style={{ fontSize: 16 }}>
              Tài khoản nhận tiền
            </Text>
            <Text variant="caption">Phí giao được chuyển về tài khoản này mỗi ngày. Tiền thu hộ (COD) nộp lại quán cuối ca.</Text>
            <Field label="Số tài khoản · Ngân hàng" placeholder="0123456789 · Vietcombank" value={bankAccount} onChangeText={setBankAccount} maxLength={60} />
          </Stack>
        ) : null}
      </View>
    </Screen>
  );
};

const labelFor = (options: ShipperSignupOptions, doc: ShipperDocument): string => options.documents.find((d) => d.id === doc)?.label ?? SHIPPER_DOCUMENT_LABELS[doc];

const Labeled = ({ label, children }: { label: string; children: ReactNode }) => (
  <View role="group" aria-label={label} style={{ gap: 8 }}>
    <Text variant="body" style={{ fontSize: 13, fontWeight: '600' }}>
      {label}
    </Text>
    {children}
  </View>
);

const Grid = ({ children }: { children: ReactNode }) => <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>{children}</View>;

const DocumentTile = ({ label, hint, value, onPress }: { label: string; hint: string; value: string | undefined; onPress: () => void }) => {
  const done = Boolean(value);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${done ? 'đã tải lên, chạm để đổi ảnh' : 'chạm để tải ảnh lên'}`}
      onPress={onPress}
      style={{
        width: 'calc(50% - 5px)' as unknown as number,
        height: 92,
        borderRadius: 14,
        borderWidth: 1.5,
        borderStyle: 'dashed',
        borderColor: done ? colors.green : colors.sky,
        backgroundColor: done ? colors.greenSoft : colors.background,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingHorizontal: 8,
      }}
    >
      <Icon name={done ? 'checkCircle' : 'camera'} size={22} color={done ? colors.green : colors.primary} strokeWidth={done ? 2.4 : 2} />
      <Text variant="label" color={colors.text} style={{ fontSize: 12 }}>
        {label}
      </Text>
      <Text variant="label" color={done ? colors.green : colors.textMuted} style={{ fontWeight: '500' }} numberOfLines={1}>
        {done ? 'Đã tải lên' : hint}
      </Text>
    </Pressable>
  );
};

const STATUS_COPY: Record<ShipperProfileView['status'], { title: string; body: string; bg: string; fg: string }> = {
  pending: { title: 'Hồ sơ đang chờ duyệt', body: 'Quán sẽ liên hệ qua số điện thoại của bạn trong 1–2 ngày làm việc để xác minh giấy tờ.', bg: colors.goldSoft, fg: colors.goldText },
  active: { title: 'Bạn đã là shipper Khao Ddi', body: 'Mở ứng dụng shipper và đăng nhập bằng số điện thoại này để bắt đầu nhận đơn.', bg: colors.greenSoft, fg: colors.greenText },
  rejected: { title: 'Hồ sơ chưa được duyệt', body: 'Hồ sơ chưa đạt yêu cầu. Vui lòng liên hệ quán để được hướng dẫn bổ sung.', bg: colors.redSoft, fg: colors.redText },
  suspended: { title: 'Tài khoản shipper đang tạm khóa', body: 'Liên hệ quán để biết lý do và mở lại tài khoản.', bg: colors.redSoft, fg: colors.redText },
};

const ApplicationStatus = ({ profile }: { profile: ShipperProfileView }) => {
  const nav = useTabNav('account', { wideOnly: true });
  const { back } = useRouter();
  const copy = STATUS_COPY[profile.status];
  return (
    <Screen background={colors.surface} nav={nav} maxWidth={720}>
      <TopBar title="Đăng ký làm shipper" onBack={() => back('/account')} background={colors.surface} />
      <View style={{ padding: 20, gap: 16 }}>
        <Intro />
        <View style={{ backgroundColor: copy.bg, borderRadius: 18, padding: 16, gap: 6 }}>
          <Text variant="title" color={copy.fg} style={{ fontSize: 20 }}>
            {copy.title}
          </Text>
          <Text variant="caption" color={copy.fg}>
            {copy.body}
          </Text>
          {profile.status === 'active' ? (
            <Button label="Mở ứng dụng shipper" icon="arrowRight" onPress={() => void Linking.openURL(SHIPPER_APP_URL)} style={{ marginTop: 8 }} />
          ) : null}
        </View>
        <View style={{ backgroundColor: colors.background, borderRadius: 18, padding: 14, gap: 8 }}>
          <SummaryRow label="Họ tên" value={profile.name} />
          <SummaryRow label="Số điện thoại" value={profile.phone} />
          <SummaryRow label="Phương tiện" value={[profile.vehicleType, profile.vehicleModel, profile.plate].filter(Boolean).join(' · ')} />
          <SummaryRow label="Khu vực" value={profile.areas.join(', ')} />
          {profile.missingDocuments.length ? <SummaryRow label="Còn thiếu" value={profile.missingDocuments.map((d) => SHIPPER_DOCUMENT_LABELS[d]).join(', ')} /> : null}
        </View>
      </View>
    </Screen>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
    <Text color={colors.textMuted} style={{ fontWeight: '400' }}>
      {label}
    </Text>
    <Text style={{ fontWeight: '600', flex: 1, textAlign: 'right' }}>{value || '—'}</Text>
  </View>
);
